import { Component, Host, h, Prop, Element, State } from "@stencil/core";

@Component({
  tag: "gx-ide-container",
  styleUrl: "container.scss",
  shadow: true
})
export class GxIdeContainer {
  // 1.OWN PROPERTIES //

  /* GENERAL */

  /**
   * By setting the title to inactive, the color will be gray instead of black.
   */
  @Prop() readonly inactiveTitle: boolean = false;

  /**
   * Displays a border all around the container
   */
  @Prop() readonly displayBorder: boolean = false;

  /**
   * Displays a border on the top
   */
  @Prop() readonly displayBorderTop: boolean = false;

  /**
   * Displays a border at the end
   */
  @Prop() readonly displayBorderEnd: boolean = false;

  /**
   * Displays a border at on the bottom
   */
  @Prop() readonly displayBorderBottom: boolean = false;

  /**
   * Displays a border at the start
   */
  @Prop() readonly displayBorderStart: boolean = false;

  /**
   * The container general padding (applies to .heading, .content, and .footer)
   */
  @Prop() readonly sectionsPadding: SectionsPadding = "m";

  /* HEADING*/

  /**
   * Removes the border bottom from the heading
   */
  @Prop() readonly noHeadingBorder: boolean = false;

  /**
   * Removes the heading gap
   */
  @Prop() readonly noHeadingGap: boolean = false;

  /**
   * Removes the padding from the heading
   */
  @Prop() readonly noHeadingPadding: boolean = false;

  /**
   * Removes the padding block end from the heading
   */
  @Prop() readonly noHeadingPaddingBlockEnd: boolean = false;

  /**
   * Adds padding to the top of the heading
   */
  @Prop() readonly headingPaddingTop: boolean = false;

  /**
   * The heading justification
   */
  @Prop() readonly headingJustify: HeadingJustify = "center";

  /**
   * It forces the .heading__title min-height to be as tall as the gxg-form-text height. This is useful when you are displaying two gx-ide-container's on the same row, both of them display a title, and one of them displays a gxg-form-text in the header (usually used to filter content). Without this property set to true, the .heading without a gxg-from-text would be shorter. At the time of writing, this is used on ww-images.
   */
  @Prop() readonly titleHeightAsInput: boolean = false;

  /**
   * Makes the header (.heading) display flex:row, instad of the default flex:column.
   */
  @Prop() readonly flexRow: boolean = false;

  /* CONTENT */

  /**
   * Makes the content a flex container
   */
  @Prop() readonly flexContent: boolean = false;

  /**
   * Removes the border-top from the content
   */
  @Prop() readonly noContentBorderTop: boolean = false;

  /**
   * Removes the padding from the content
   */
  @Prop() readonly noContentPadding: boolean = false;

  /**
   * Removes the gap from the content
   */
  @Prop() readonly noContentGap: boolean = false;

  /**
   * Align items
   */
  @Prop() readonly alignItems: AlignItems = undefined;

  /**
   * Justify content
   */
  @Prop() readonly justifyContent: JustifyContent = undefined;

  /**
   * Display border end on the content
   */
  @Prop() readonly contentBorderEnd: boolean = false;

  /* FOOTER ABOVE */

  /**
   * Makes the above footer vertical padding slimmer
   */
  @Prop() readonly slimmerAboveFooter: boolean = false;

  /**
   * Removes the above padding from the footer
   */
  @Prop() readonly noAboveFooterPadding: boolean = false;

  /**
   * Removes the above border top
   */
  @Prop() readonly noBorderAboveFooter: boolean = false;

  /* FOOTER > START and FOOTER > END*/

  /**
   * Makes the footer vertical padding slimmer
   */
  @Prop() readonly slimmerFooter: boolean = false;

  /**
   * Removes the padding from the footer
   */
  @Prop() readonly noFooterPadding: boolean = false;

  /**
   * The footer justification
   */
  @Prop() readonly footerJustify: FooterJustify = "end";

  /**
   * Removes the border top from the footer
   */
  @Prop() readonly noBorderFooter: boolean = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeContainerElement;

  // 3.STATE() VARIABLES //

  /**
   * A boolean variable indicating if the component has or not any content for the 'header' slot
   */
  @State() hasHeaderSlot = false;

  /**
   * A boolean variable indicating if the component has or not any content slotted content (regular slotted content, without 'slot' attribute)
   */
  @State() hasSlottedContent = false;

  /**
   * A boolean variable indicating if the component has or not any content for the 'footer' slot
   */
  @State() hasFooterSlot = false;

  /**
   * A boolean variable indicating if the component has or not any content for the 'footer' slot
   */
  @State() hasAboveFooterSlot = false;

  /**
   * A boolean variable indicating that the container only has the heading section
   */
  @State() hasOnlyHeading = false;

  /**
   * A boolean variable indicating that the container only has the content section
   */
  @State() hasOnlyContent = false;

  /**
   * A boolean variable indicating that the container only has the footer section
   */
  @State() hasOnlyFooter = false;

  // 4.PUBLIC PROPERTY API //

  // 5.EVENTS (EMIT) //

  // 6.COMPONENT LIFECYCLE EVENTS //

  componentWillLoad() {
    this.evaluateSlots();
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private evaluateSlots() {
    // header
    const headerSlot = this.el.querySelectorAll(':scope > [slot="header"]');
    if (headerSlot.length) {
      this.hasHeaderSlot = true;
    }
    // content
    const slottedContent = this.el.querySelectorAll(":scope >  :not([slot])");
    if (slottedContent.length) {
      this.hasSlottedContent = true;
    }
    // footer
    const footerAboveSlot = this.el.querySelectorAll(
      ':scope >  [slot="footer-above"]'
    );
    const footerStartSlot = this.el.querySelectorAll(
      ':scope >  [slot="footer-start"]'
    );
    const footerEndSlot = this.el.querySelectorAll(
      ':scope >  [slot="footer-end"]'
    );
    if (footerStartSlot.length || footerEndSlot.length) {
      this.hasFooterSlot = true;
    }
    if (footerAboveSlot.length) {
      this.hasAboveFooterSlot = true;
    }
  }

  private evaluateSections() {
    // has only heading
    if (
      !this.hasSlottedContent &&
      !this.hasFooterSlot &&
      !this.hasAboveFooterSlot
    ) {
      this.hasOnlyHeading = true;
    }
    // has only content
    if (
      this.hasSlottedContent &&
      !this.hasHeaderSlot &&
      !this.hasAboveFooterSlot &&
      !this.hasFooterSlot
    ) {
      this.hasOnlyContent = true;
    }
    // has only footer
    if (!this.hasSlottedContent && !this.hasAboveFooterSlot) {
      this.hasOnlyFooter = true;
    }
  }

  private headingClasses = (): string | { [className: string]: boolean } => {
    return {
      "heading": true,
      "heading--inactive": this.inactiveTitle,
      "heading--no-border": this.noHeadingBorder || this.hasOnlyHeading,
      [`heading--justify-${this.headingJustify}`]: true,
      "heading--no-padding": this.noHeadingPadding,
      "heading--no-padding-block-end": this.noHeadingPaddingBlockEnd,
      "heading--no-gap": this.noHeadingGap,
      "heading--padding-top": this.headingPaddingTop,
      "heading--form-text-height": this.titleHeightAsInput,
      "heading--flex-row": this.flexRow
    };
  };

  private contentClasses = (): string | { [className: string]: boolean } => {
    return {
      "content": true,
      "content--flex": this.flexContent,
      "content--no-padding": this.noContentPadding,
      "content--no-gap": this.noContentGap,
      "content--no-border-top": this.noContentBorderTop,
      "content--border-end": this.contentBorderEnd,
      [`content--align-items-${this.alignItems}`]:
        this.alignItems !== undefined,
      [`content--justify-content-${this.justifyContent}`]:
        this.justifyContent !== undefined
    };
  };

  private aboveFooter = (): any | null => {
    return this.hasAboveFooterSlot ? (
      <footer
        class={{
          "footer-above": true,
          "footer-above--no-border-top": this.noBorderAboveFooter,
          "footer-above--no-padding": this.noAboveFooterPadding,
          "footer-above--slimmer": this.slimmerFooter
        }}
      >
        <slot name="footer-above"></slot>
      </footer>
    ) : null;
  };

  private footer = (): any | null => {
    return this.hasFooterSlot ? (
      <footer
        class={{
          "footer": true,
          "footer--no-border":
            this.hasOnlyFooter ||
            !this.hasSlottedContent ||
            this.noBorderFooter,
          [`footer--justify-${this.footerJustify}`]: true,
          [`footer--justify-${this.footerJustify}`]: true,
          "footer--no-padding": this.noFooterPadding,
          "footer--slimmer": this.slimmerFooter
        }}
      >
        <div class="footer__start">
          <slot name="footer-start"></slot>
        </div>
        <div class="footer__end">
          <slot name="footer-end"></slot>
        </div>
      </footer>
    ) : null;
  };

  // 10.RENDER() FUNCTION //

  render() {
    this.evaluateSections();
    const result = [
      this.hasHeaderSlot ? (
        <header class={this.headingClasses()}>
          <div class="heading__inner-wrapper">
            <slot name="header"></slot>
          </div>
        </header>
      ) : null,
      this.hasSlottedContent ? (
        <div class={this.contentClasses()} part="content">
          <slot></slot>
        </div>
      ) : null,
      this.aboveFooter(),
      this.footer()
    ];

    return (
      <Host>
        <div
          class={{
            "container": true,
            [`container--padding-${this.sectionsPadding}`]: true,
            "container--display-border": this.displayBorder,
            "container--display-border-top": this.displayBorderTop,
            "container--display-border-end": this.displayBorderEnd,
            "container--display-border-bottom": this.displayBorderBottom,
            "container--display-border-start": this.displayBorderStart,
            "container--only-content": this.hasOnlyContent,
            "container--no-header": !this.hasHeaderSlot,
            "container--no-footer":
              !this.hasFooterSlot && !this.hasAboveFooterSlot
          }}
        >
          {result}
        </div>
      </Host>
    );
  }
}

export type HeadingJustify = "start" | "center" | "end";
export type FooterJustify = "start" | "center" | "end";
export type SectionsPadding = "s" | "m" | "l";
export type AlignItems = "start" | "center" | "end";
export type JustifyContent = "start" | "center" | "end";
